import { useState } from "react";
import {
  installGuideText,
  notificationGuideText,
  type Environment,
  type OnboardingStep,
} from "@safers/pwa";

interface Props {
  step: OnboardingStep;
  env: Environment;
  busy: boolean;
  onDismiss: () => void;
}

/**
 * 근로자용 설치·알림 온보딩 카드.
 *
 * 기존에는 설치 모달(전체 화면 차단) · 설치 배너 · 알림 섹션이 따로 있었다.
 * 세 개를 하나로 합치고, 차단형 모달 대신 상단 카드로 바꾼다 —
 * QR 로 들어온 근로자가 체감온도만 확인하려는 경우까지 모달로 막을 이유가 없다.
 * 조회는 언제나 가능해야 하고 온보딩은 권유다. (#176)
 *
 * 어떤 환경에서도 침묵하지 않는다. 설치도 알림도 불가능한 기기라면
 * "불가능하다"는 사실 자체를 알린다 — 못 받는 사람이 그걸 모르는 것이 가장 위험하다.
 */
export function OnboardingCard({ step, env, busy, onDismiss }: Props) {
  // 차단됨 안내를 펼쳤는가. 평소엔 접어 둔다 — 카드가 화면 맨 위라 지면을 적게 먹어야 한다.
  const [guideOpen, setGuideOpen] = useState(false);
  if (step.kind === "done") return null;

  const view = (() => {
    switch (step.kind) {
      case "switch-browser":
        return step.target === "chrome"
          ? {
              icon: "🌐",
              title: "Chrome으로 열어주세요",
              body: "지금 브라우저에서는 앱 설치와 알림이 제한됩니다.",
              action: step.openInChrome && {
                label: "Chrome으로 열기",
                onClick: step.openInChrome,
              },
            }
          : {
              icon: "🧭",
              title: "Safari로 열어주세요",
              body: "iPhone은 Safari에서만 앱 설치와 알림을 사용할 수 있습니다. 주소를 복사해 Safari에서 열어주세요.",
              action: null,
            };

      case "install":
        return step.manual
          ? {
              icon: "📲",
              title: env.os === "ios" ? "홈 화면에 추가" : "앱으로 설치하면 더 편해요",
              body: installGuideText(env.os),
              action: null,
            }
          : {
              icon: "📲",
              title: "앱으로 설치하기",
              body: "홈 화면에 추가하면 더 빠르게 열고 알림을 안정적으로 받아요.",
              action: step.promptInstall && {
                label: "홈 화면에 추가",
                onClick: () => void step.promptInstall?.(),
              },
            };

      // 정상 경로에서는 WorkerPage 가 전체 화면(AppHandoffScreen)으로 가로채므로
      // 여기까지 오지 않는다. 카드로 떨어지는 경우를 대비한 안전망.
      case "open-app":
        return {
          icon: "🏠",
          title: "앱으로 열어주세요",
          body: "홈 화면의 SAFERS 아이콘으로 열면 위험 알림을 안정적으로 받습니다.",
          action: null,
        };

      case "enable-notification":
        return {
          icon: "🔔",
          title: "위험 알림 받기",
          body: "현장의 위험 경보·공지를 휴대폰 알림으로 받습니다. 한 번만 설정하면 됩니다.",
          action: {
            label: busy ? "설정 중…" : "알림 받기",
            onClick: () => void step.request(),
          },
        };

      case "push-unavailable":
        if (step.reason === "denied") {
          return {
            icon: "🔕",
            title: "알림이 차단되어 있습니다",
            // 거부된 뒤에는 앱이 권한을 다시 물을 수 없다(requestPermission 이 즉시 denied 로
            // 끝난다). 그래서 이 액션은 권한 요청이 아니라 "어디를 눌러라"를 펴는 것이다.
            body: guideOpen
              ? notificationGuideText(env.os, env.browser)
              : "위험 경보를 받으려면 이 사이트의 알림을 다시 허용해야 합니다.",
            action: {
              label: guideOpen ? "접기" : "허용 방법",
              onClick: () => setGuideOpen((v) => !v),
            },
          };
        }
        return {
          icon: "⚠️",
          title: "이 기기에서는 알림을 받을 수 없습니다",
          body: "현장 방송과 관리자 안내를 확인해주세요. 작업 전 이 화면에서 최근 안내를 확인하는 것을 권장합니다.",
          action: null,
        };
    }
  })();

  return (
    <section className="rounded-2xl bg-brand p-4 text-white shadow-md">
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 flex-none items-center justify-center rounded-xl bg-white/20 text-xl">
          {view.icon}
        </div>

        <div className="min-w-0 flex-1">
          <div className="text-sm font-bold">{view.title}</div>
          <p className="mt-0.5 text-xs leading-relaxed text-white/90">{view.body}</p>

          {view.action && (
            <button
              type="button"
              onClick={view.action.onClick}
              disabled={busy}
              className="mt-2.5 w-full rounded-xl bg-white py-2.5 text-sm font-bold text-brand disabled:opacity-60"
            >
              {view.action.label}
            </button>
          )}
        </div>

        <button
          type="button"
          onClick={onDismiss}
          aria-label="안내 닫기"
          className="flex-none text-lg leading-none text-white/70"
        >
          ✕
        </button>
      </div>
    </section>
  );
}
